import React from 'react';

import classNames from 'classnames';
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const EmblaApiContext = createContext<EmblaCarouselType | null>(null);

export function useEmblaApi() {
    return useContext(EmblaApiContext);
}

type RenderProps = {
    carousel: JSX.Element;
    arrowLeft?: JSX.Element;
    arrowRight?: JSX.Element;
    progressBar?: JSX.Element;
    selectedIndex?: number;
};

type Props = {
    children: ({
        carousel,
        arrowLeft,
        arrowRight,
        progressBar,
        selectedIndex,
    }: RenderProps) => JSX.Element;
    slides: React.ReactNode;
    overflowHidden?: boolean;
    options?: EmblaOptionsType;
    inline?: boolean;
};

export function Carousel({
    children,
    slides,
    overflowHidden,

    options,
    inline,
}: Props) {
    const [viewportRef, embla] = useEmblaCarousel(
        {
            align: 'start',
            containScroll: 'trimSnaps',
            dragFree: true,
            ...options,
        },
        [WheelGesturesPlugin()]
    );

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

    const onSelect = useCallback(() => {
        if (!embla) return;

        setSelectedIndex(embla.selectedScrollSnap());
        setPrevBtnEnabled(embla.canScrollPrev());
        setNextBtnEnabled(embla.canScrollNext());
    }, [embla]);

    const onScroll = useCallback(() => {
        if (!embla) return;

        setSelectedIndex(embla.selectedScrollSnap());
    }, [embla]);

    useEffect(() => {
        if (!embla) return;
        embla.on('select', onSelect);
        embla.on('scroll', onScroll);
        embla.on('resize', onSelect);
        onSelect();

        return () => {
            embla.off('select', onSelect);
            embla.off('scroll', onScroll);
            embla.off('resize', onSelect);
        };
    }, [embla, onSelect, onScroll]);

    useEffect(() => {
        embla?.reInit();
        onScroll();
    }, [embla, onScroll]);

    const canDrag = prevBtnEnabled || nextBtnEnabled;

    useEffect(() => {
        embla?.reInit({
            watchDrag: canDrag,
        });
    }, [canDrag, embla]);

    const carousel = (
        <div className="relative w-full">
            <div
                className={classNames('w-full', {
                    'overflow-hidden': overflowHidden,
                    'w-full': !inline,
                })}
                ref={viewportRef}
            >
                <div className="flex transform-gpu">{slides}</div>
            </div>
        </div>
    );

    return (
        <EmblaApiContext.Provider value={embla ?? null}>
            {children({
                carousel,
                selectedIndex,
            })}
        </EmblaApiContext.Provider>
    );
}
